import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Building, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuthConfig } from '../auth/usePublicSignup';
import CompanyInvitations from './CompanyInvitations';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;
const ENDPOINT = `${API_URL}/admin/companies`;

export const COMPANIES_NOTE = 'Company ID is generated automatically. Users join only through explicit '
  + 'invitations; matching an email domain does not grant access.';

/**
 * PILOT-ACCESS-002: the platform super-admin's Company registry. Lists every Company (name,
 * primary domain, internal id) and creates one from a name and a primary domain; the server
 * normalizes and de-duplicates the domain and generates the id. Rendered, and fetched, only
 * for the super-admin: for anyone else this component is nothing and makes no request.
 *
 * PILOT-ACCESS-003A: when the server reports the invitation gate on, each Company with a primary
 * domain gets an "Invite member" action opening its invitations panel. With the gate off (or
 * unknown) no invitation control is offered at all.
 */
const CompaniesRegistry = ({ token }) => {
  const [companies, setCompanies] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const authConfig = useAuthConfig();
  const invitationsOn = authConfig?.invitations_enabled === true;

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(ENDPOINT, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(Array.isArray(response.data) ? response.data : []);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);


  const handleCreate = async (event) => {
    event.preventDefault();
    if (creating) return;
    setFormError('');
    if (!name.trim() || !domain.trim()) {
      setFormError('Company name and primary domain are required');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(
        ENDPOINT,
        { name, primary_domain: domain },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Company created: ${response.data.name}`);
      setName('');
      setDomain('');
      await load();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : 'The company could not be created');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card className="world-card" data-testid="companies-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
            <Building className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <CardTitle className="text-xl text-white">Companies</CardTitle>
            <CardDescription className="text-slate-400">{COMPANIES_NOTE}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
          data-testid="companies-create-form">
          <div className="space-y-1">
            <Label htmlFor="company-registry-name" className="text-sm text-slate-300">Company name</Label>
            <Input id="company-registry-name" value={name} onChange={(e) => setName(e.target.value)}
              maxLength={200} className="world-input" data-testid="companies-name-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="company-registry-domain" className="text-sm text-slate-300">Primary domain</Label>
            <Input id="company-registry-domain" value={domain} onChange={(e) => setDomain(e.target.value)}
              placeholder="company.example" maxLength={253} className="world-input"
              data-testid="companies-domain-input" />
          </div>
          <Button type="submit" disabled={creating} className="world-btn-primary"
            data-testid="companies-create-button">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create company'}
          </Button>
        </form>
        {formError && (
          <p className="text-sm text-red-400" role="alert" data-testid="companies-form-error">{formError}</p>
        )}

        {loadError && (
          <p className="text-sm text-slate-400" data-testid="companies-load-error">The company list could not be loaded.</p>
        )}
        {companies && companies.length === 0 && (
          <p className="text-sm text-slate-400" data-testid="companies-empty">No companies yet.</p>
        )}
        {companies && companies.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="companies-table">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/10">
                  <th className="py-2 pr-4 font-medium">Company</th>
                  <th className="py-2 pr-4 font-medium">Primary domain</th>
                  <th className="py-2 font-medium">Company ID</th>
                  {invitationsOn && <th className="py-2 font-medium" aria-label="Invitations" />}
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id} className="border-b border-white/5" data-testid={`companies-row-${company.id}`}>
                    <td className="py-2 pr-4 text-white">{company.name}</td>
                    <td className="py-2 pr-4 text-slate-300">{company.primary_domain || '—'}</td>
                    <td className="py-2 font-mono text-xs text-slate-400 select-all break-all">{company.id}</td>
                    {invitationsOn && (
                      <td className="py-2 pl-3 text-right whitespace-nowrap">
                        {company.primary_domain ? (
                          <Button type="button" variant="outline" size="sm"
                            onClick={() => setSelectedId(company.id)} data-testid={`companies-invite-${company.id}`}>
                            Invite member
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-500">Needs a primary domain</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {invitationsOn && selectedId && companies && companies.some((c) => c.id === selectedId) && (
          <CompanyInvitations
            key={selectedId}
            company={companies.find((c) => c.id === selectedId)}
            token={token}
            onClose={() => setSelectedId(null)}
          />
        )}
      </CardContent>
    </Card>
  );
};

// PILOT-ACCESS-003A: the gate lives outside the registry so that for anyone but the super-admin
// no hook runs and no request is made at all — not even the public /auth/config read.
const CompaniesCard = () => {
  const { token, isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return null;
  return <CompaniesRegistry token={token} />;
};

export default CompaniesCard;
